import { type JSX, useState } from 'react';

import { Avatar, EloDivider, EloIcon, EloTooltip, type EloIconType, Menu, Text, VStack } from '@plurall/elo-v3';

import { ProfileMenuCardFactory, ProfileMenuContentFactory, ProfileMenuItemFactory } from './ProfileMenu.factory';
import { type ProfileMenuActionItem, useProfileMenu } from './useProfileMenu';
import { AppNavIconButton } from '../AppNavIconButton/AppNavIconButton';

type ProfileMenuTriggerProps = {
  isOpen: boolean;
  triggerRef: React.Ref<HTMLButtonElement>;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

function ProfileMenuTrigger({ isOpen, triggerRef, onKeyDown }: ProfileMenuTriggerProps): JSX.Element {
  return (
    <EloTooltip
      content='Meu perfil'
      position='bottom'
      openDelay={300}
      size='small'
      disabled={isOpen}
      closeOnPointerDown={false}
      closeOnClick={false}>
      <Menu.Trigger asChild>
        <AppNavIconButton
          ref={triggerRef}
          aria-label='Meu perfil'
          icon='user-female-circle'
          iconHover='user-female-circle-fill'
          isActive={isOpen}
          onKeyDown={onKeyDown}
        />
      </Menu.Trigger>
    </EloTooltip>
  );
}

type ProfileMenuItemProps = {
  item: ProfileMenuActionItem;
  icon: EloIconType;
  isHovered: boolean;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onSelect: () => void;
};

function ProfileMenuItem({
  item,
  icon,
  isHovered,
  onPointerEnter,
  onPointerLeave,
  onSelect,
}: ProfileMenuItemProps): JSX.Element {
  const [isFocused, setIsFocused] = useState(false);
  const iconName = (isHovered || isFocused ? `${icon}-fill` : icon) as EloIconType;

  return (
    <ProfileMenuItemFactory
      value={item.value}
      tabIndex={0}
      isLogout={item.isLogout}
      onSelect={onSelect}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}>
      <EloIcon
        icon={iconName}
        color='var(--elo-colors-foreground-brand)'
        flexShrink={0}
      />
      <span>{item.label}</span>
    </ProfileMenuItemFactory>
  );
}

export function ProfileMenu(): JSX.Element {
  const {
    isOpen,
    setIsOpen,
    hoveredItem,
    setHoveredItem,
    contentRef,
    triggerRef,
    openedFromKeyboardRef,
    firstName,
    initial,
    visibleItems,
    handleSelect,
    handleTriggerKeyDown,
    handleContentKeyDown,
  } = useProfileMenu();

  return (
    <Menu.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        const nextOpen = open ?? false;
        setIsOpen(nextOpen);

        if (!nextOpen) {
          openedFromKeyboardRef.current = false;
        }
      }}
      positioning={{ placement: 'bottom-end', gutter: 16 }}
      size='md'
      variant='subtle'>
      <ProfileMenuTrigger
        isOpen={isOpen}
        triggerRef={triggerRef}
        onKeyDown={handleTriggerKeyDown}
      />
      <Menu.Positioner>
        <ProfileMenuContentFactory
          ref={contentRef}
          minW='280px'
          p={0}
          overflow='hidden'
          onOpenAutoFocus={(event: { preventDefault: () => void }) => {
            if (openedFromKeyboardRef.current) {
              event.preventDefault();
              triggerRef.current?.focus();
            }
          }}
          onKeyDown={handleContentKeyDown}>
          <ProfileMenuCardFactory p='2'>
            <VStack
              gap='2'
              align='center'
              p='4'
              pb='2'
              textAlign='center'>
              <Avatar.Root
                size='xl'
                borderRadius='full'
                background='var(--elo-colors-background-brand-soft)'
                border='1px solid var(--elo-colors-border-brand-primary)'
                height='40px'
                width='40px'
                color='var(--elo-colors-foreground-brand)'>
                <Avatar.Fallback
                  fontSize='md'
                  fontWeight='semibold'
                  color='inherit'>
                  {initial}
                </Avatar.Fallback>
              </Avatar.Root>
              <VStack gap='1'>
                <Text
                  fontSize='var(--elo-font-sizes-font-size-large)'
                  lineHeight='1.2'
                  fontWeight='var(--elo-font-weights-font-weight-semi-bold)'
                  color='var(--elo-colors-text-primary)'
                  textTransform='none'>
                  Olá, {firstName || '—'}
                </Text>
              </VStack>
            </VStack>
            <EloDivider
              my='3'
              borderRadius='full'
              width='calc(100% - 2rem)'
              boxSizing='border-box'
              flexShrink={0}
              alignSelf='stretch'
              minWidth={0}
              maxWidth='100%'
              marginInline='1rem'
              borderBlockStart='2px solid var(--elo-colors-border-primary)'
            />
            <VStack
              gap='0'
              align='stretch'>
              {visibleItems.map((item) => (
                <ProfileMenuItem
                  key={item.value}
                  item={item}
                  icon={item.icon}
                  isHovered={hoveredItem === item.value}
                  onPointerEnter={() => setHoveredItem(item.value)}
                  onPointerLeave={() => setHoveredItem(null)}
                  onSelect={() => {
                    void handleSelect(item);
                  }}
                />
              ))}
            </VStack>
          </ProfileMenuCardFactory>
        </ProfileMenuContentFactory>
      </Menu.Positioner>
    </Menu.Root>
  );
}
